/**
 * Pricing Page
 *
 * Fetches subscription plans from the backend API and redirects to
 * Stripe Checkout for payment. Polls for payment success after checkout.
 * Shown after login for users who haven't subscribed yet.
 * Skip button goes to /chat.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import apiClient from "../services/api/client";

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#D4915A"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ArrowIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  API plan type                                                      */
/* ------------------------------------------------------------------ */

interface ApiPlan {
  id: string;
  name: string;
  slug: string;
  description: string;
  price: string;
  billingInterval: string;
  features: Record<string, unknown>;
  trialDays: number;
  stripePriceId: string | null;
}

/* ------------------------------------------------------------------ */
/*  Hardcoded Free plan                                                */
/* ------------------------------------------------------------------ */

const FREE_PLAN_FEATURES = [
  "Limited chats per month",
  "Limited autonomous runs",
  "Basic AI assistance",
  "Access to web app + extension",
];

/**
 * Format a feature value for display
 */
function formatFeatureValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString();
  return String(value);
}

const EXTENSION_ID = import.meta.env.VITE_EXTENSION_ID;

function tryOpenExtensionSidePanel() {
  if (typeof chrome !== "undefined" && chrome.runtime && EXTENSION_ID) {
    chrome.runtime.sendMessage(
      EXTENSION_ID,
      { action: "OPEN_SIDE_PANEL" },
      () => {
        if (chrome.runtime.lastError) {
          console.debug(
            "Extension not available:",
            chrome.runtime.lastError.message,
          );
        }
      },
    );
  }
}

/* ------------------------------------------------------------------ */
/* Helper: billing label                                               */
/* ------------------------------------------------------------------ */
function billingLabel(interval: string) {
  switch (interval?.toUpperCase()) {
    case "YEARLY":
    case "ANNUAL":
      return "year";
    case "WEEKLY":
      return "week";
    default:
      return "month";
  }
}

/* ------------------------------------------------------------------ */
/* Component                                                         */
/* ------------------------------------------------------------------ */

export function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [plans, setPlans] = useState<ApiPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoadingId, setCheckoutLoadingId] = useState<string>("");
  const [sessionId, setSessionId] = useState<string>("");
  const [checkoutError, setCheckoutError] = useState<string>("");
  const checkoutAttemptsRef = useRef(0);
  const MAX_CHECKOUT_ATTEMPTS = 3;

  /* ---- Fetch plans from API ---- */
  useEffect(() => {
    let cancelled = false;

    const fetchPlans = async () => {
      try {
        const response = await apiClient.get<{ data: ApiPlan[] }>(
          "/api/v1/subscription/plans",
        );
        if (!cancelled) {
          // Only show paid plans (stripePriceId is set); free plan is hardcoded
          const paidPlans = response.data.filter(
            (p) => p.stripePriceId !== null,
          );
          setPlans(paidPlans);
        }
      } catch (err) {
        console.error("Failed to load plans:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPlans();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---- Create Stripe checkout session & open ---- */
  const handleCheckout = useCallback(
    async (planId: string) => {
      if (checkoutLoadingId) return; // prevent double-clicks

      if (checkoutAttemptsRef.current >= MAX_CHECKOUT_ATTEMPTS) {
        setCheckoutError(
          "Too many failed attempts. Please refresh the page and try again.",
        );
        return;
      }

      setCheckoutLoadingId(planId);
      setCheckoutError("");
      checkoutAttemptsRef.current++;

      try {
        const response = await apiClient.post<{
          data: { url: string; sessionId: string };
        }>("/api/v1/subscription/checkout", { planId });

        const { url, sessionId: sid } = response.data;
        setSessionId(sid);
        checkoutAttemptsRef.current = 0; // reset on success

        window.open(url, "_blank", "noopener,noreferrer");
      } catch (err) {
        console.error("Checkout error:", err);
        if (checkoutAttemptsRef.current >= MAX_CHECKOUT_ATTEMPTS) {
          setCheckoutError(
            "Checkout failed after multiple attempts. Please try again later.",
          );
        } else {
          setCheckoutError("Checkout failed. Please try again.");
        }
      } finally {
        setCheckoutLoadingId("");
      }
    },
    [checkoutLoadingId],
  );

  /* ---- Poll for payment success ---- */
  const pollRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    pollRef.current = false;

    const POLL_INTERVAL = 5000;
    const MAX_WAIT = 120000;
    let pollTimeoutId: number;

    const overallTimeoutId = window.setTimeout(() => {
      pollRef.current = true;
      setSessionId("");
    }, MAX_WAIT);

    const poll = async () => {
      if (pollRef.current) return;

      try {
        const response = await apiClient.post<{
          data: { plan?: { isActive: boolean }; status?: string };
        }>("/api/v1/subscription/success", { sessionId });

        const isActive = response.data?.plan?.isActive;
        if (!pollRef.current && isActive) {
          pollRef.current = true;
          clearTimeout(overallTimeoutId);
          sessionStorage.setItem("user_subscribed", "true");
          setSessionId("");
          tryOpenExtensionSidePanel();
          navigate("/chat", { replace: true });
          return;
        }
      } catch {
        // Payment not yet confirmed — keep polling
      }

      if (!pollRef.current) {
        pollTimeoutId = window.setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();

    return () => {
      pollRef.current = true;
      clearTimeout(pollTimeoutId);
      clearTimeout(overallTimeoutId);
    };
  }, [sessionId, navigate]);

  const handleStartFree = () => {
    sessionStorage.setItem("user_subscribed", "true");

    // Notify extension that user already saw & skipped pricing on web
    if (typeof chrome !== "undefined" && chrome.runtime && EXTENSION_ID) {
      chrome.runtime.sendMessage(
        EXTENSION_ID,
        { action: "PRICING_SKIPPED" },
        () => {
          if (chrome.runtime.lastError) {
            console.debug(
              "Extension not available:",
              chrome.runtime.lastError.message,
            );
          }
        },
      );
    }

    tryOpenExtensionSidePanel();
    navigate("/chat", { replace: true });
  };

  return (
    <div className="flex flex-col h-screen overflow-y-auto custom-scrollbar bg-[#FAF6F1] dark:bg-[#1A2D47] relative">
      {/* ===== Header ===== */}
      <div className="pt-10 pb-6 flex flex-col items-center px-6">
        <h1 className="text-3xl lg:text-4xl font-bold text-[#1D1B19] dark:text-white text-center">
          Choose Your Plan
        </h1>
        <p className="mt-2 text-base text-[#6B6B6B] dark:text-[#8B9DB5] text-center max-w-md">
          {user?.name ? `Welcome, ${user.name}! ` : ""}Get access to all
          features. Upgrade or cancel anytime.
        </p>
      </div>

      {/* ===== Loading / Content ===== */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-[#D4915A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* ===== Plan cards ===== */}
          <div className="flex-1 flex items-start lg:items-center justify-center px-6 py-8">
            <div
              className={`w-full max-w-5xl grid grid-cols-1 gap-6 lg:gap-8 ${
                plans.length === 1
                  ? "md:grid-cols-2"
                  : `md:grid-cols-${plans.length + 1}`
              }`}
            >
              {/* ===== Free Plan Card (hardcoded) ===== */}
              <div className="relative flex flex-col rounded-2xl border border-[#E0D5C7] dark:border-[#2C3A4E] bg-white dark:bg-[#1B2B3D] p-6 lg:p-8 transition-all duration-300">
                <p className="text-xs font-semibold tracking-widest text-[#9B9B9B] dark:text-[#8B9DB5] uppercase mb-4">
                  Get Started
                </p>

                <h2 className="text-2xl font-bold text-[#1D1B19] dark:text-white mb-1">
                  Free
                </h2>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#1D1B19] dark:text-white">
                    $0
                  </span>
                  <span className="text-sm text-[#6B6B6B] dark:text-[#8B9DB5] ml-1">
                    / month
                  </span>
                </div>

                <div className="border-t border-[#E0D5C7] dark:border-[#2C3A4E] my-2" />

                <ul className="flex flex-col gap-3 mt-4 mb-8 flex-1">
                  {FREE_PLAN_FEATURES.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center gap-3 text-sm text-[#4A4A4A] dark:text-[#C0C9D6]"
                    >
                      <CheckIcon />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={handleStartFree}
                  className="w-full h-12 rounded-xl font-semibold text-sm flex items-center justify-center border border-[#E0D5C7] dark:border-[#3A4A5E] text-[#1D1B19] dark:text-white hover:bg-[#F5F0E8] dark:hover:bg-[#243854] transition-colors mt-auto"
                >
                  Continue with Free Plan
                </button>
              </div>

              {/* ===== Dynamic Plan Cards from API ===== */}
              {plans.map((plan) => {
                const isLoading = checkoutLoadingId === plan.id;
                const isPolling = !!sessionId;
                return (
                  <div
                    key={plan.id}
                    className="relative flex flex-col rounded-2xl border-2 border-[#D4915A] bg-white dark:bg-[#1B2B3D] p-6 lg:p-8 transition-all duration-300 shadow-lg shadow-[#D4915A]/10"
                  >
                    <p className="text-xs font-semibold tracking-widest text-[#D4915A] uppercase mb-4">
                      {plan.name}
                    </p>

                    <h2 className="text-2xl font-bold text-[#1D1B19] dark:text-white mb-1">
                      {plan.slug
                        .split("-")
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(" ")}
                    </h2>

                    <div className="mb-6">
                      <span className="text-4xl font-bold text-[#1D1B19] dark:text-white">
                        ${plan.price}
                      </span>
                      <span className="text-sm text-[#6B6B6B] dark:text-[#8B9DB5] ml-1">
                        / {billingLabel(plan.billingInterval)}
                      </span>
                    </div>

                    <div className="border-t border-[#E0D5C7] dark:border-[#2C3A4E] my-2" />

                    <ul className="flex flex-col gap-3 mt-4 mb-8 flex-1">
                      {Object.values(plan.features ?? {}).map((value, idx) => (
                        <li
                          key={idx}
                          className="flex items-center gap-3 text-sm text-[#4A4A4A] dark:text-[#C0C9D6]"
                        >
                          <CheckIcon />
                          {formatFeatureValue(value)}
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => handleCheckout(plan.id)}
                      disabled={
                        isLoading ||
                        isPolling ||
                        checkoutAttemptsRef.current >= MAX_CHECKOUT_ATTEMPTS
                      }
                      className={`w-full h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200 touch-manipulation active:scale-[0.97] bg-gradient-to-r from-[#D4915A] to-[#C07E48] hover:shadow-lg hover:shadow-[#D4915A]/30 text-white mt-auto ${
                        isLoading ||
                        isPolling ||
                        checkoutAttemptsRef.current >= MAX_CHECKOUT_ATTEMPTS
                          ? "opacity-60 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : isPolling ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Waiting for payment…
                        </>
                      ) : (
                        <>
                          Upgrade to{" "}
                          {plan.slug
                            .split("-")
                            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                            .join(" ")}
                          <ArrowIcon />
                        </>
                      )}
                    </button>

                    {checkoutError && (
                      <p className="mt-3 text-xs text-red-400 text-center">
                        {checkoutError}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ===== Footer ===== */}
          <div className="flex justify-center gap-2 text-xs text-[#9B9B9B] dark:text-[#6B7F99] pb-6">
            <span>Terms of Service</span>
            <span aria-hidden="true">·</span>
            <span>Privacy Policy</span>
          </div>
        </>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { 
          width: 8px; 
        }
        .custom-scrollbar::-webkit-scrollbar-track { 
          background: transparent; 
        }
        .custom-scrollbar::-webkit-scrollbar-thumb { 
          background: rgba(139, 157, 181, 0.4); 
          border-radius: 10px; 
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { 
          background: rgba(139, 157, 181, 0.7); 
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(139, 157, 181, 0.4) transparent;
        }
      `}</style>
    </div>
  );
}
